import { Center } from '@chakra-ui/react';
import { loadFonts } from 'hooks/useFont';
import { useWindowSize } from 'hooks/useWindowSize';
import { setActivePage, useAppDispatch, useAppSelector } from 'modules/Editor/store';
import {
  selectInfographHeightPx,
  selectInfographWidthPx,
  selectPageBackgroundString,
} from 'modules/Editor/store/infographSelector';
import { loadInfograph } from 'modules/Editor/store/infographSlice';
import { findFontsInPage } from 'modules/Editor/store/infographSlice.helpers';
import { setFontsLoaded } from 'modules/Editor/store/pageControlSlice';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { Page } from 'modules/common/components/Page';
import { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { InfographState } from 'types/infographTypes';
import { ReadOnlyStructureTreeListWidgetRenderer } from 'widgets/WidgetRenderer';

import { DEFAULT_PREVIEW_SCALE } from '../../DesignGenerator.config';

type DesignPreviewProps = {
  fitWidth?: boolean;
  infographState: InfographState;
};

export const DesignPreview = ({ infographState, fitWidth = true }: DesignPreviewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: windowWidth, height: windowHeight } = useWindowSize();

  // Scale to fit preview inside container
  const [scale, setScale] = useState(DEFAULT_PREVIEW_SCALE);

  const widthPx = useAppSelector(selectInfographWidthPx);
  const heightPx = useAppSelector(selectInfographHeightPx);
  const activePageId = useAppSelector(selectActivePage);
  const pageBackground = useAppSelector(selectPageBackgroundString(activePageId));

  // Set scale of preview to fit inside container
  const resetScale = useCallback(() => {
    const containerWidth = containerRef.current?.clientWidth;
    const containerHeight = containerRef.current?.clientHeight;

    if (fitWidth && containerWidth) {
      // If fitWidth is true, scale to fit width of the current container
      const widthRatio = containerWidth / widthPx;
      setScale(widthRatio);
    } else if (containerWidth && containerHeight) {
      // If fitWidth is false, resize to fit the minimum widthRatio or heightRatio of the current container.
      const widthRatio = containerWidth / widthPx;
      const heightRatio = containerHeight / heightPx;
      setScale(Math.min(widthRatio, heightRatio));
    } else {
      setScale(DEFAULT_PREVIEW_SCALE);
    }
  }, [fitWidth, widthPx, heightPx]);

  useEffect(() => resetScale(), [resetScale, windowWidth, windowHeight]);

  return (
    <DesignPreviewLoader infographState={infographState}>
      <Center
        w='full'
        // 27rem is for the CardGenerator
        h={fitWidth ? 'full' : '27rem'}
        ref={containerRef}
      >
        {activePageId && (
          <Page
            testId={activePageId}
            className='page-container'
            bg={pageBackground}
            width={widthPx}
            height={heightPx}
            zoom={scale}
            borderRadius='none'
            boxShadow='none'
            overflow='hidden'
          >
            <ReadOnlyStructureTreeListWidgetRenderer pageId={activePageId} />
          </Page>
        )}
      </Center>
    </DesignPreviewLoader>
  );
};

type DesignPreviewLoaderProps = {
  infographState: InfographState;
  children: ReactNode;
};

const DesignPreviewLoader = ({ infographState, children }: DesignPreviewLoaderProps) => {
  const dispatch = useAppDispatch();

  // Load infograph in store
  useEffect(() => {
    const pageId = infographState.pageOrder[0] || '';
    dispatch(loadInfograph(infographState));
    dispatch(setActivePage({ pageId }));

    loadFonts(findFontsInPage(infographState, pageId)).finally(() => {
      dispatch(setFontsLoaded(true));
    });
  }, [infographState, dispatch]);

  return <>{children}</>;
};
